"use client"

import React, { useEffect, useState } from "react";
import useMqtt from '@/lib/useMqtt';
import ReactFlow, { MiniMap, Controls, Node, Edge } from "reactflow";
import dagre from "dagre";
import "reactflow/dist/style.css";

const colorMap: Record<string, string> = {
  planner: "#ffb3b3",
  worker: "#b3d1ff",
  tool: "#b3ffb3",
  memory: "#d1b3ff",
};

export default function GraphPage() {
  const [nodes, setNodes] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);

  // layout helpers
  const nodeWidth = 150;
  const nodeHeight = 50;
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const getLayoutedElements = (nodes:any[], edges:any[]) => {
    dagreGraph.setGraph({ rankdir: 'LR' });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
      return node;
    });
    return { nodes: layoutedNodes, edges };
  };

  const processData = (data: any) => {
    const initialNodes = data.nodes.map((n: any) => ({
      id: n.id,
      data: {
        label:
          n.label +
          (n.status ? ` (${n.status})` : "") +
          (n.timestamp ? `\n${new Date(n.timestamp).toLocaleTimeString()}` : ""),
      },
      style: { background: colorMap[n.type] || "#eee", padding: 10, borderRadius: 4 },
    }));
    const initialEdges = data.edges.map((e: any, idx: number) => ({
      id: `e${idx}`,
      source: e.from,
      target: e.to,
      label: e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : undefined,
      animated: true,
      style: { stroke: "#888" },
    }));
    const { nodes: layouted, edges: layoutEdges } = getLayoutedElements(initialNodes, initialEdges);
    setNodes(layouted);
    setEdges(layoutEdges);
  };

  useEffect(() => {
    async function fetchGraph() {
      const res = await fetch("/api/graph");
      const data = await res.json();
      processData(data);
    }
    fetchGraph();
  }, []);

  useMqtt('graph', (msg) => {
    try {
      if (msg.type === 'node' && msg.node) {
        setNodes((nds) => {
          const idx = nds.findIndex((n) => n.id === msg.node.id);
          let updated = [...nds];
          const nobj = {
            id: msg.node.id,
            data: { label: msg.node.label + (msg.node.status ? ` (${msg.node.status})` : '') + (msg.node.timestamp ? `\n${new Date(msg.node.timestamp).toLocaleTimeString()}` : '') },
            style: { background: colorMap[msg.node.type] || '#eee', padding: 10, borderRadius: 4 },
          };
          if (idx >= 0) {
            updated[idx] = { ...updated[idx], ...nobj };
          } else {
            updated.push(nobj);
          }
          const layouted = getLayoutedElements(updated, edges).nodes;
          setEdges((es) => getLayoutedElements(updated, es).edges);
          return layouted;
        });
      } else if (msg.type === 'edge' && msg.edge) {
        setEdges((eds) => {
          const id = `e${eds.length}`;
          const eobj: any = {
            id,
            source: msg.edge.from,
            target: msg.edge.to,
            style: { stroke: '#888' },
          };
          if (msg.edge.timestamp) {
            eobj.label = new Date(msg.edge.timestamp).toLocaleTimeString();
          }
          const updated = [...eds, eobj];
          const layouted = getLayoutedElements(nodes, updated).edges;
          setNodes((nds) => getLayoutedElements(nds, updated).nodes);
          return layouted;
        });
      }
    } catch (e) {
      // ignore
    }
  });

  return (
    <div style={{ height: "100vh", background: "#fafafa" }}>
      <ReactFlow nodes={nodes} edges={edges} fitView>
        <MiniMap />
        <Controls />
      </ReactFlow>
    </div>
  );
}
